import { useEffect, useRef, useState } from "react";

/**
 * `useSticky` is a React hook that monitors whether a specified HTML element
 * is in a "sticky" state based on its position in the viewport.
 *
 * @template T A generic parameter that extends HTMLElement, which allows users
 * to specify the type of the HTML element that the ref is linked to.
 *
 * @returns {Readonly<[React.RefObject<T>, boolean]>} A tuple where:
 * - The first item is a ref object which should be bound to the element
 *   to be observed.
 * - The second item is a boolean state indicating whether the element is
 *   in a sticky state.
 *
 * @example
 * const [stickyRef, isSticky] = useSticky<HTMLDivElement>();
 *
 * // In JSX
 * <div ref={stickyRef}>Content</div>
 *
 * // Use `isSticky` to conditionally render or apply styles elsewhere
 *
 * @remarks
 * - The sticky state is determined based on the element’s offset from the top
 *   of the viewport compared to its computed `top` property value.
 * - Event listeners are added to the `scroll`, `resize`, and `orientationchange`
 *   events to recalculate the sticky state. They are cleaned up on unmount
 *   or if the hook is rerun.
 */
export function useSticky<T extends HTMLElement>() {
  const stickyRef = useRef<T>(null);
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    // Observe when ref enters or leaves sticky state
    // rAF https://stackoverflow.com/questions/41740082/scroll-events-requestanimationframe-vs-requestidlecallback-vs-passive-event-lis
    function observe() {
      if (!stickyRef.current) return;
      const refPageOffset = stickyRef.current.getBoundingClientRect().top;
      const stickyOffset = parseInt(getComputedStyle(stickyRef.current).top);
      const stickyActive = Math.floor(refPageOffset) <= Math.floor(stickyOffset);

      if (stickyActive && !isSticky) setIsSticky(true);
      else if (!stickyActive && isSticky) setIsSticky(false);
    }
    observe();

    // Bind events
    document.addEventListener("scroll", observe);
    window.addEventListener("resize", observe);
    window.addEventListener("orientationchange", observe);

    return () => {
      document.removeEventListener("scroll", observe);
      window.removeEventListener("resize", observe);
      window.removeEventListener("orientationchange", observe);
    };
  }, [isSticky]);

  return [stickyRef, isSticky] as const;
}
